import React, { useState, useEffect, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { Alert, AlertDescription } from './ui/alert';
import {
  Package,
  Truck,
  ChevronDown,
  ChevronUp,
  Loader2,
  X,
  Download,
} from 'lucide-react';
import warehouseService from '../services/warehouseService';
import { formatBeijingDate } from '../utils/dateUtils';

/**
 * WarehouseMovementDrawer - Reusable drawer component for displaying warehouse movement records
 * 
 * Features:
 * - 70vw wide side drawer
 * - Filter movements by source_id and movement type (inbound/outbound)
 * - Table display with collapsible product detail rows
 * - Status badges and source type badges
 * - Empty state handling
 * - Pagination (10 items per page)
 * - View only - no action buttons
 */
const WarehouseMovementDrawer = ({
  open,
  onClose,
  movementType,
  sourceId,
  sourceReference,
  title,
}) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [exportingId, setExportingId] = useState(null);

  const page_size = 10;

  const loadMovements = useCallback(async () => {
    if (!sourceId) return;

    try {
      setLoading(true);
      setError('');

      const params = {
        movement_type: movementType,
        source_id: sourceId,
        page: page + 1,
        page_size,
      };

      const response = await warehouseService.getMovements(params);
      setMovements(response.movements || []);
      setTotal(response.pagination?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || '加载记录失败');
    } finally {
      setLoading(false);
    }
  }, [movementType, sourceId, page]);

  useEffect(() => {
    if (open && sourceId) {
      loadMovements();
    }
  }, [open, sourceId, loadMovements]);

  useEffect(() => {
    setPage(0);
  }, [sourceId, movementType]);

  const handleToggleExpand = useCallback((movementId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [movementId]: !prev[movementId],
    }));
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportDelivery = async (movement) => {
    if (exportingId) return;
    try {
      setExportingId(movement._id);
      setError('');
      await warehouseService.exportDeliveryNote(movement._id);
    } catch (err) {
      setError(err.response?.data?.error || '导出失败');
    } finally {
      setExportingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: '待确认', variant: 'outline' },
      confirmed: { label: '已确认', variant: 'default' },
      completed: { label: '已完成', variant: 'default' },
      cancelled: { label: '已取消', variant: 'destructive' },
    };

    const config = statusConfig[status] || { label: status, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getInboundSourceTypeBadge = (sourceType) => {
    const sourceTypeConfig = {
      purchase_order: { label: '采购入库', color: 'bg-blue-100 text-blue-800' },
      production_order: { label: '成品入库', color: 'bg-green-100 text-green-800' },
      production_order_remaining: { label: '工单余料入库', color: 'bg-amber-100 text-amber-800' },
      remaining_bar: { label: '余料入库', color: 'bg-amber-100 text-amber-800' },
      sales_order: { label: '销售退货', color: 'bg-purple-100 text-purple-800' },
      manual: { label: '手动入库', color: 'bg-gray-100 text-gray-800' },
      cross_client_transfer: { label: '调拨入库', color: 'bg-indigo-100 text-indigo-800' },
      subcontract_inbound: { label: '外协入库', color: 'bg-green-100 text-green-800' },
      subcontract_order: { label: '外协回料', color: 'bg-amber-100 text-amber-800' },
    };

    const config = sourceTypeConfig[sourceType] || { label: sourceType || '其他', color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getOutboundSourceTypeBadge = (sourceType) => {
    const sourceTypeConfig = {
      sales_order: { label: '销售出库', color: 'bg-blue-100 text-blue-800' },
      manual: { label: '手动出库', color: 'bg-gray-100 text-gray-800' },
      sample: { label: '样品出库', color: 'bg-purple-100 text-purple-800' },
      production_order: { label: '生产领料', color: 'bg-green-100 text-green-800' },
      cross_client_transfer: { label: '调拨出库', color: 'bg-indigo-100 text-indigo-800' },
    };

    const config = sourceTypeConfig[sourceType] || { label: sourceType || '其他', color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getSourceTypeBadge = (sourceType) => {
    if (movementType === 'inbound') {
      return getInboundSourceTypeBadge(sourceType);
    }
    return getOutboundSourceTypeBadge(sourceType);
  };

  const calculateTotalQuantity = (products) => {
    if (!products || products.length === 0) return 0;
    return products.reduce((sum, product) => sum + (product.quantity_received || product.quantity || 0), 0);
  };

  const drawerTitle = title || (movementType === 'inbound' ? '入库记录' : '出库记录');
  const drawerDescription = sourceReference
    ? `${movementType === 'inbound' ? '采购订单' : '销售订单'}: ${sourceReference}`
    : (movementType === 'inbound' ? '查看入库记录' : '查看出库记录');

  const totalPages = Math.ceil(total / page_size);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-[70vw] max-w-none p-0 overflow-hidden flex flex-col"
      >
        <SheetHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="flex items-center gap-2">
                {movementType === 'inbound' ? (
                  <Package className="h-5 w-5 text-primary" />
                ) : (
                  <Truck className="h-5 w-5 text-primary" />
                )}
                {drawerTitle}
              </SheetTitle>
              <SheetDescription>
                {drawerDescription}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto p-6">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setError('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : movements.length === 0 ? (
                <div className="flex flex-col items-center py-8">
                  {movementType === 'inbound' ? (
                    <Package className="h-12 w-12 text-muted-foreground mb-2" />
                  ) : (
                    <Truck className="h-12 w-12 text-muted-foreground mb-2" />
                  )}
                  <span className="text-muted-foreground">
                    {movementType === 'inbound' ? '暂无入库记录' : '暂无出库记录'}
                  </span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted border-b">
                      <tr>
                        {movementType === 'inbound' ? (
                          <>
                            <th className="w-12 p-3" />
                            <th className="text-left p-3 font-medium">单号</th>
                            <th className="text-left p-3 font-medium">状态</th>
                            <th className="text-left p-3 font-medium">来源类型</th>
                            <th className="text-left p-3 font-medium">来源单号</th>
                            <th className="text-left p-3 font-medium">供应商</th>
                            <th className="text-left p-3 font-medium">客户</th>
                            <th className="text-left p-3 font-medium">仓库</th>
                            <th className="text-left p-3 font-medium">总数量</th>
                            <th className="text-left p-3 font-medium">日期</th>
                          </>
                        ) : (
                          <>
                            <th className="w-12 p-3" />
                            <th className="text-left p-3 font-medium">单号</th>
                            <th className="text-left p-3 font-medium">状态</th>
                            <th className="text-left p-3 font-medium">来源</th>
                            <th className="text-left p-3 font-medium">客户</th>
                            <th className="text-left p-3 font-medium">仓库</th>
                            <th className="text-left p-3 font-medium">产品数</th>
                            <th className="text-left p-3 font-medium">日期</th>
                            <th className="text-left p-3 font-medium">操作</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {movements.map((movement) => (
                        <Collapsible
                          key={movement._id}
                          open={expandedRows[movement._id]}
                          onOpenChange={() => handleToggleExpand(movement._id)}
                          asChild
                        >
                          <React.Fragment>
                            <tr className="border-b hover:bg-muted">
                              <td className="p-3">
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    {expandedRows[movement._id] ? (
                                      <ChevronUp className="h-4 w-4" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4" />
                                    )}
                                  </Button>
                                </CollapsibleTrigger>
                              </td>
                              <td className="p-3">
                                <span className="font-medium">
                                  {movement._id.slice(-6).toUpperCase()}
                                </span>
                              </td>
                              <td className="p-3">{getStatusBadge(movement.status)}</td>
                              <td className="p-3">{getSourceTypeBadge(movement.source_type)}</td>

                              {movementType === 'inbound' ? (
                                <>
                                  <td className="p-3">
                                    {movement.source_reference || '-'}
                                  </td>
                                  <td className="p-3">{movement.supplier_name || '-'}</td>
                                  <td className="p-3">{movement.client_name || '-'}</td>
                                  <td className="p-3">{movement.warehouse_location || 'main'}</td>
                                  <td className="p-3">{calculateTotalQuantity(movement.products)}</td>
                                  <td className="p-3">
                                    {formatBeijingDate(movement.movement_date)}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="p-3">
                                    {movement.source_type === 'sales_order'
                                      ? movement.source_reference || '-'
                                      : movement.source_reference || '手动创建'}
                                  </td>
                                  <td className="p-3">{movement.client_name || '-'}</td>
                                  <td className="p-3">{movement.warehouse_location || 'main'}</td>
                                  <td className="p-3">{movement.products?.length || 0}</td>
                                  <td className="p-3">
                                    {formatBeijingDate(movement.movement_date)}
                                  </td>
                                  <td className="p-3">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      title="导出销售出库单"
                                      disabled={exportingId === movement._id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleExportDelivery(movement);
                                      }}
                                    >
                                      {exportingId === movement._id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Download className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </td>
                                </>
                              )}
                            </tr>

                            <tr>
                              <td colSpan={movementType === 'inbound' ? 10 : 9} className="p-0">
                                <CollapsibleContent>
                                  <div className="p-4 bg-muted border-b">
                                    <h4 className="text-sm font-semibold mb-3">
                                      {movementType === 'inbound' ? '入库产品明细' : '出库产品明细'}
                                    </h4>
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b">
                                          {movementType === 'inbound' ? (
                                            <>
                                              <th className="text-left p-2 font-medium">产品编码</th>
                                              <th className="text-left p-2 font-medium">产品名称</th>
                                              <th className="text-left p-2 font-medium">订购数量</th>
                                              <th className="text-left p-2 font-medium">收货数量</th>
                                              <th className="text-left p-2 font-medium">单位</th>
                                              <th className="text-left p-2 font-medium">单价</th>
                                              <th className="text-left p-2 font-medium">小计</th>
                                              <th className="text-left p-2 font-medium">库位</th>
                                              <th className="text-left p-2 font-medium">批次号</th>
                                            </>
                                          ) : (
                                            <>
                                              <th className="text-left p-2 font-medium">产品编码</th>
                                              <th className="text-left p-2 font-medium">产品名称</th>
                                              <th className="text-left p-2 font-medium">计划数量</th>
                                              <th className="text-left p-2 font-medium">实际数量</th>
                                              <th className="text-left p-2 font-medium">单位</th>
                                              <th className="text-left p-2 font-medium">库位</th>
                                              <th className="text-left p-2 font-medium">类型</th>
                                            </>
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {movement.products?.map((product, index) => (
                                          <tr key={index} className="border-b">
                                            <td className="p-2">{product.product_id}</td>
                                            <td className="p-2">{product.name}</td>

                                            {movementType === 'inbound' ? (
                                              <>
                                                <td className="p-2">{product.quantity || 0}</td>
                                                <td className="p-2">
                                                  <span
                                                    className={
                                                      product.quantity_received < product.quantity
                                                        ? 'text-warning'
                                                        : product.quantity_received > product.quantity
                                                        ? 'text-destructive'
                                                        : ''
                                                    }
                                                  >
                                                    {product.quantity_received}
                                                  </span>
                                                </td>
                                                <td className="p-2">{product.unit}</td>
                                                <td className="p-2">
                                                  ¥{product.unit_price?.toFixed(2) || '0.00'}
                                                </td>
                                                <td className="p-2">
                                                  ¥
                                                  {(
                                                    (product.quantity_received || 0) *
                                                    (product.unit_price || 0)
                                                  ).toFixed(2)}
                                                </td>
                                                <td className="p-2">
                                                  {product.location || movement.warehouse_location}
                                                </td>
                                                <td className="p-2">{product.batch_number || '-'}</td>
                                              </>
                                            ) : (
                                              <>
                                                <td className="p-2">{product.quantity || 0}</td>
                                                <td className="p-2">
                                                  {product.quantity_received || product.quantity || 0}
                                                </td>
                                                <td className="p-2">{product.unit}</td>
                                                <td className="p-2">
                                                  {product.location || movement.warehouse_location}
                                                </td>
                                                <td className="p-2">
                                                  {product.is_leftover ? (
                                                    <span
                                                      className="text-green-600 font-medium"
                                                      title={`余料：${product.leftover_code || ''}`}
                                                    >
                                                      余料
                                                      {product.leftover_code && ` (${product.leftover_code})`}
                                                    </span>
                                                  ) : product.is_remaining ? (
                                                    <span className="text-blue-600 font-medium">尾料</span>
                                                  ) : product.category === '工具' ||
                                                    product.category === '成品' ? (
                                                    <span className="text-gray-500">成品</span>
                                                  ) : (
                                                    <span className="text-gray-500">新料</span>
                                                  )}
                                                </td>
                                              </>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>

                                    {movement.notes && (
                                      <p className="mt-3 text-sm text-muted-foreground">
                                        备注：{movement.notes}
                                      </p>
                                    )}
                                    {movement.created_by && (
                                      <p className="mt-2 text-xs text-muted-foreground">
                                        创建时间：{formatBeijingDate(movement.created_at)}
                                      </p>
                                    )}
                                  </div>
                                </CollapsibleContent>
                              </td>
                            </tr>
                          </React.Fragment>
                        </Collapsible>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex justify-center py-4 border-t mt-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 0}
                >
                  上一页
                </Button>
                <span className="text-sm text-muted-foreground px-4">
                  第 {page + 1} / {totalPages} 页
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  下一页
                </Button>
              </div>
            </div>
          )}

          <p className="mt-4 text-sm text-muted-foreground">
            显示 {movements.length} / {total} 条记录
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WarehouseMovementDrawer;
